import {useMemo} from 'react';
import {useQuery} from '@apollo/client';
import {
  DefaultBankAccounts,
  DefaultBankAccountsVariables,
} from '../../schema-types/DefaultBankAccounts';
import validateUUID from '../../utils/validateUUID';
import {GET_DEFAULT_BANK_ACCOUNTS} from './defaultBankAccountQueries';

export type DefaultBankAccountHolder = {
  id: string;
  simplifiedName: string | null;
  defaultBankAccountId: string | null;
};

// The account's primary and secondary holders, and the default bank account each one has.
export const useContactDefaultBankAccounts = (accountId: string | undefined) => {
  const {data, loading, error, refetch} = useQuery<
    DefaultBankAccounts,
    DefaultBankAccountsVariables
  >(GET_DEFAULT_BANK_ACCOUNTS, {
    variables: {accountId: validateUUID(accountId)!},
    skip: !accountId,
  });

  const holders = useMemo<DefaultBankAccountHolder[]>(
    () =>
      (data?.defaultBankAccounts.contacts ?? []).map((contact) => ({
        id: contact.id,
        simplifiedName: contact.simplifiedName,
        defaultBankAccountId: contact.defaultBankAccount?.id ?? null,
      })),
    [data]
  );

  const holdersByDefault = useMemo(() => {
    const byBankAccount: Record<string, DefaultBankAccountHolder[]> = {};
    holders.forEach((holder) => {
      if (holder.defaultBankAccountId) {
        byBankAccount[holder.defaultBankAccountId] = [
          ...(byBankAccount[holder.defaultBankAccountId] ?? []),
          holder,
        ];
      }
    });
    return byBankAccount;
  }, [holders]);

  const holdersWithoutDefault = useMemo(
    () => holders.filter((holder) => !holder.defaultBankAccountId),
    [holders]
  );

  return {
    isEnabled: data?.defaultBankAccounts.isEnabled ?? false,
    holders,
    holdersByDefault,
    holdersWithoutDefault,
    loading,
    error,
    refetch,
  };
};
